import { useState, useEffect, useCallback } from 'react';

interface OrientationState {
  isPortrait: boolean;
  isLandscape: boolean;
  isMobile: boolean;
  angle: number;
}

/**
 * Custom hook to detect device orientation.
 * Uses Screen Orientation API with fallback to matchMedia.
 */
export function useOrientation(): OrientationState {
  const getOrientation = useCallback((): OrientationState => {
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    ) || window.innerWidth < 768;

    let angle = 0;
    let isPortrait = true;

    // Try Screen Orientation API first
    if (screen.orientation) {
      angle = screen.orientation.angle;
      isPortrait = screen.orientation.type.includes('portrait');
    } else {
      // Fallback: use window dimensions
      isPortrait = window.innerHeight > window.innerWidth;
      angle = isPortrait ? 0 : 90;
    }

    return {
      isPortrait,
      isLandscape: !isPortrait,
      isMobile,
      angle,
    };
  }, []);

  const [orientation, setOrientation] = useState<OrientationState>(getOrientation);

  useEffect(() => {
    const handleChange = () => {
      setOrientation(getOrientation());
    };

    // Screen Orientation API
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleChange);
    }

    // Fallback: resize event
    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleChange);
      }
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, [getOrientation]);

  return orientation;
}
